import AccountDropdown from 'app/components/header/AccountDropdown';
import appConfig from 'app/config/client';
import { RunningIn } from 'app/config/types/DeployInfo';
import RestrictContent from 'app/containers/auth/RestrictContent';
import { useCss } from 'app/utils/css';
import React, { FC, ReactNode } from 'react';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import LanguageDropdown from 'app/containers/header/LanguageDropdown';
import UnauthenticatedContent from 'app/containers/auth/UnauthenticatedContent';
import LoginLink from 'app/components/auth/LoginLink';
import DevServerDetails from 'app/components/header/DevServerDetails';
import { DisplayRoleConsumer } from '../DisplayRoleContext';
import { TEACHER } from 'app/constants/DisplayRoles';
import { useI18n } from 'app/components/localization';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import BrandMaterialsNavigationItems from 'app/components/header/navigation/BrandMaterialsNavigationItems';
import { isStaffMember, isTeacher, getAuthUser } from 'app/models/auth/selectors';
import { connect } from 'react-redux';
import { AppState } from 'app/store';
import CommonNavigationItems from 'app/components/header/navigation/CommonNavigationItems';
import HeaderView, { IHeaderViewProps, HeaderStack } from 'app/components/header/HeaderView';
import { NavigationType } from 'app/components/header/navigation/Navigation';
import StickyContainer from 'app/components/header/StickyContainer';
import CallErrors from 'app/components/call/CallErrors';
import GlobalCallTabbar from 'app/components/call/GlobalCallTabbar';
import InContentVideos from 'app/components/call/InContentVideos';
import { getFullUrl } from 'app/utils/routes';
import { owlPath } from 'app/components/svgs/IknowIcon';
import { svgDataUrl } from 'app/utils/dataUrl';
import CopyMeetingLinkNavigationItem from 'app/components/header/navigation/CopyMeetingLinkNavigationItem';
import { IUser } from 'app/constants/schemas/User';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { BookingType } from 'app/models/rtc/booking/constants';
import { MeetingLandingRoute, TeacherRecruitmentRoute } from 'app/constants/Routes';
import { getNavLinkStyles } from 'app/components/presentation/nav/styles';
import { useHoverState } from '@engoo/react-hover-state';
import UserSensitiveLink from '../auth/UserSensitiveLink';
import { useHistoryLocation } from '../router/History';
import BaseAnchor from '../router/BaseAnchor';
import { createGuid } from 'app/types/guid';

let devServerOverrides = {};

if (appConfig.deployInfo.runningIn !== RunningIn.Production) {
  const devServerBackdropFilterID = createGuid();
  const devServerBackdrop = `
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="50" viewBox="227 0 1024 2048">
      <defs>
        <filter id="${devServerBackdropFilterID}">
          <feGaussianBlur in="SourceGraphic" stdDeviation="75" color-interpolation-filters="sRGB" />
        </filter>
      </defs>
      <g filter="url(#${devServerBackdropFilterID})" opacity="0.3" fill="#ff8b00">
        <path d="${owlPath}" />
        <path d="${owlPath}" transform="translate(-512 1024)" />
        <path d="${owlPath}" transform="translate( 512 1024)" />
      </g>
    </svg>
    `;

  devServerOverrides = {
    backgroundImage: `url(${svgDataUrl(devServerBackdrop)})`,
  };
}

export const enum HeaderVariant {
  /**
   * Disables sign in button when an org is defined
   */
  Default,
  /**
   * Disables sign in button always
   */
  NoLogin,
  /**
   * Enables sign in button always
   */
  ForceLogin,
}

export interface IHeaderProps extends Omit<IHeaderViewProps, 'navigationType' | '_devServerOverrides'> {
  // Injected from redux
  user: IUser;
  variant?: HeaderVariant;
  showTeacherIndicator?: boolean;
  embedInContentVideos?: boolean;
  disableFixedHeaderVideo?: boolean;
  routeLogoToTutorSite?: boolean;
  stickies?: ReactNode;
}

/**
 * This component encapsulates the logic for the Header.
 */
const Header: FC<IHeaderProps> = ({
  user,
  variant,
  showTeacherIndicator,
  embedInContentVideos = false,
  disableFixedHeaderVideo = false,
  routeLogoToTutorSite = false,
  stickies,
  ...rest
}) => {
  const location = useHistoryLocation();
  const css = useCss();
  const { t } = useI18n();
  const bookingState = useBookingState();
  const breakpoint = usePageBreakpoint();

  const [loginLinkHovered, loginLinkHoverRef] = useHoverState();

  const isBooking = Boolean(bookingState);
  const isMeeting = bookingState?.bookingType === BookingType.Meeting;
  const isMobile = breakpoint < NumericBreakpoint.Wide;
  // Since we only want legal links to show in calls, we do not have anything else to show in the dropdown
  // navigation, except when you're in the bellbird landing page where we have the "Create a Call" button.
  const isMeetingLandingRoute = location.pathname === MeetingLandingRoute.getPathname();

  const showDrawerNavigation = isStaffMember(user) || isTeacher(user) || (!isMobile && isBooking && !isMeeting);
  const showDropdownNavigation = isMobile && !showDrawerNavigation && (isBooking || isMeetingLandingRoute);

  return (
    <DisplayRoleConsumer>
      {({ role }) => (
        <>
          <HeaderView
            navigationType={
              showDrawerNavigation
                ? NavigationType.Drawer
                : showDropdownNavigation
                ? NavigationType.Dropdown
                : NavigationType.Header
            }
            navigationItems={<CommonNavigationItems />}
            leftHeaderNavigationItems={isBooking && <BrandMaterialsNavigationItems />}
            rightHeaderNavigationItems={<CopyMeetingLinkNavigationItem />}
            renderLogoWrapper={props =>
              routeLogoToTutorSite ? (
                <BaseAnchor
                  href={TeacherRecruitmentRoute.getPathname()}
                  {...props}
                />
              ) : isMeeting ? (
                <BaseAnchor
                  href={getFullUrl({
                    ...location,
                    pathname: MeetingLandingRoute.getPathname(),
                    search: undefined,
                  })}
                  {...props}
                />
              ) : (
                <UserSensitiveLink className={props.className}>
                  {props.children}
                </UserSensitiveLink>
              )
            }
            rightContent={
              <>
                {showTeacherIndicator && role === TEACHER && (
                  <div
                    className={css('teacherView', {
                      display: 'flex',
                      alignItems: 'center',
                      color: COLOR_PALETTE.red.base,
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '1',
                      backgroundColor: COLOR_PALETTE.grey.white,
                      marginRight: '1em',
                    })}
                  >
                    {t('admin.header.teacher-view')}
                  </div>
                )}

                {appConfig.deployInfo.runningIn === RunningIn.DevServer && <DevServerDetails />}

                {variant === HeaderVariant.ForceLogin || variant === HeaderVariant.Default ? (
                  // Display the sign in link only when forced
                  <UnauthenticatedContent>
                    <LoginLink
                      className={css(
                        'loginLink',
                        getNavLinkStyles({ active: false, selected: false, hovered: loginLinkHovered, logo: false }),
                      )}
                      getRef={loginLinkHoverRef}
                    />
                  </UnauthenticatedContent>
                ) : null}

                <LanguageDropdown />

                <RestrictContent>
                  <AccountDropdown />
                </RestrictContent>
              </>
            }
            _devServerOverrides={devServerOverrides}
            {...rest}
          />
          <StickyContainer zIndex={HeaderStack.StickyContainer} disableFixedHeaderVideo={disableFixedHeaderVideo}>
            <div
              className={css('stickies', {
                position: 'relative',
                zIndex: 1,
              })}
            >
              {stickies}
            </div>
            <div
              className={css('globalStickies', {
                position: 'relative',
                zIndex: 0,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
              })}
            >
              <CallErrors />
              <GlobalCallTabbar />
            </div>
          </StickyContainer>
          {embedInContentVideos && (
            <div
              className={css('callContainer', {
                position: 'relative',
                zIndex: HeaderStack.CallContainer,
              })}
            >
              <InContentVideos />
            </div>
          )}
        </>
      )}
    </DisplayRoleConsumer>
  );
};

export default connect((state: AppState) => ({
  user: getAuthUser(state),
}))(Header);
